import type { ReactNode } from 'react';
import { formatHours, parseHours, parseMinutes, type Metafield } from '@forecastify/shared';
import { Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { DateField } from '../date-field';
import { MetafieldSection } from '../metafields/metafield-section';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ ONE LOGGING FORM — feedback round 4 item 20, and it is the batch.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * **Three forms asked three different questions about the same row.** The Időrögzítés quick-log
 * took hours, a note and an overtime type and never asked when the work started. The task's own
 * widget took a date, hours and a note, and could not mark overtime — so the one feature whose only
 * entrance is a worklog form had no entrance from the screen people log against a ticket from. The
 * calendar's dialog took a START and an END and neither of the other two, because a drag produces a
 * range; it could not mark overtime either.
 *
 * So the same act produced a different row depending on where you did it, and two of the three
 * quietly could not reach a feature. **The form is one component now, and it asks: date, start
 * time, duration, overtime, description.**
 *
 * ⚠️⚠️ **THERE IS NO END TIME, AND THE DATA MODEL AGREED ALL ALONG.** `worklogs` stores
 * `started_at time` and `hours numeric` — `0017` argues why, and there has never been an end
 * column. The calendar's `from`/`to` was a range converted at the dialog's edge; what it stored was
 * always a start and a length. Asking for the end was asking for a number the product does not
 * keep, and it made the calendar the only screen where *"how long did this take"* had no answer on
 * the form.
 *
 * ⚠️ **The start time defaults to 09:00 and is not required to be meaningful.** Most people log a
 * duration and do not care when it began; the calendar cares, because a block has to be drawn
 * somewhere. A default keeps both honest — the field is always there, and nobody has to fill it in.
 *
 * ⚠️ **The TASK is the caller's, and that is the one thing this does not draw.** The task widget
 * already knows its task, the quick-log has a search box with a result list, and the calendar has a
 * picker. One control that tried to be all three would be a worse version of each. `lead` is where
 * the caller puts whatever it uses.
 */
export interface WorklogDraft {
  /** Present when editing an existing row. */
  readonly id?: string;
  readonly version?: number;
  readonly taskId: string;
  /** Wire date. */
  readonly workedOn: string;
  /** `HH:MM`. ⚠️ Never empty — `START_OF_DAY` is what an unset one means. */
  readonly startedAt: string;
  /** As typed. Parsed by the shared helper: `8`, `8h`, `8:00`, `8,0`, `90m`. */
  readonly hours: string;
  readonly note: string;
  /**
   * ┌────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ OVERTIME OR NOT — A BOOLEAN, BECAUSE THE KIND IS NOT THIS PERSON'S CALL.     │
   * └────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **THIS WAS A TYPE PICKER AND IT LET PEOPLE PRICE THEIR OWN HOURS.** *Túlóra · 1.5×*,
   * *Hétvégi · 2×*, *Ünnepnapi · 2.5×* were three options on the form somebody logs time with.
   * The product owner's ruling of 20 Sep: the requester asserts that an hour WAS overtime; which
   * kind it is, and therefore what it is worth, is the approver's decision. `0147` carries it.
   *
   * ⚠️ **P3-5 still holds — overtime is raised from the entry**, and this is still the only place
   * a claim can be created. `PHASE-3.md` §1: _"Túlórát az időrögzítésnél jelölsz."_ What changed
   * is how much the marking decides.
   */
  readonly overtime: boolean;
  /** C10's values, keyed by the field's own key. */
  readonly metafields: Record<string, unknown>;
}

/** ⚠️ The mockup's own default, and the hour a working day starts in every schedule we ship. */
export const START_OF_DAY = '09:00';

export function emptyWorklogDraft(workedOn: string): WorklogDraft {
  return {
    taskId: '',
    workedOn,
    startedAt: START_OF_DAY,
    hours: '',
    note: '',
    overtime: false,
    metafields: {},
  };
}

/**
 * Whether a draft could be saved, and what it would save.
 *
 * ⚠️ **Hours are the only required field**, because the task is the caller's business and the date
 * and start both carry defaults. Empty is not zero here: an empty box means somebody has not
 * answered yet, and `parseHours` returning `null` means they answered with something that is not a
 * duration. Both refuse; neither becomes `0`, which would file a row saying the work took no time.
 */
export function worklogHours(draft: WorklogDraft): number | null {
  if (draft.hours.trim() === '') return null;
  const parsed = parseHours(draft.hours);
  return parsed === null || parsed <= 0 ? null : parsed;
}

/**
 * The body of a `POST /worklogs` or a `PATCH`, from a draft.
 *
 * ⚠️ **`startedAt` goes as `null` when it is the default and nothing else was said.** A row whose
 * start is 09:00 because nobody touched the field is not the same fact as one somebody set to
 * 09:00, and the column is nullable precisely so the two can differ — the calendar draws a block
 * for the first at nine o'clock either way, and a report that asks _"who starts early"_ should not
 * count a defaulted field as an answer.
 */
export function worklogBody(draft: WorklogDraft, hours: number): Record<string, unknown> {
  return {
    workedOn: draft.workedOn,
    hours,
    startedAt: draft.startedAt === '' ? null : draft.startedAt,
    note: draft.note.trim() === '' ? null : draft.note.trim(),
    ...(Object.keys(draft.metafields).length === 0 ? {} : { metafields: draft.metafields }),
  };
}

/**
 * `09:00` + `2:30` → `11:30`, for anything that needs to draw the block the draft describes.
 *
 * ⚠️ **Derived, never stored.** The end of a worklog is its start plus its length; keeping it as a
 * third number is how the calendar and the list came to disagree about the same row.
 */
export function endOf(startedAt: string, hours: number): string {
  const start = parseMinutes(startedAt) ?? 540;
  const end = start + Math.round(hours * 60);
  return `${String(Math.floor(end / 60) % 24).padStart(2, '0')}:${String(end % 60).padStart(2, '0')}`;
}

export function WorklogFields({
  draft,
  onChange,
  overtimeOffered,
  metafields = [],
  layout = 'row',
  lead,
  showDate = true,
}: {
  readonly draft: WorklogDraft;
  readonly onChange: (next: WorklogDraft) => void;
  /**
   * ⚠️ **Whether the workspace uses overtime at all.** The TYPES are no longer the form's business
   * — the approver picks one — but a workspace that has defined none cannot raise a claim, so the
   * control is absent rather than offering something the server will refuse.
   */
  readonly overtimeOffered: boolean;
  /** The workspace's `worklog` metafields. Empty when none are defined. */
  readonly metafields?: readonly Metafield[];
  /**
   * `row` for a strip at the foot of a list, `stack` inside a dialog.
   *
   * ⚠️ **A layout, not a second form.** The fields, their order, their defaults and their
   * validation are the same in both — which is the whole of item 20. What differs is how much
   * horizontal room the caller has.
   */
  readonly layout?: 'row' | 'stack';
  /** The task control, which the caller owns — see the note on the component. */
  readonly lead?: ReactNode;
  /**
   * ⚠️ Off only where the date is not the form's to choose: the calendar's dialog belongs to a day
   * the drag already picked, and the quick-log belongs to the day the strip has selected. Both show
   * it elsewhere on the screen, and a second copy that could disagree is worse than none.
   */
  readonly showDate?: boolean;
}) {
  const t = useT();
  const hours = worklogHours(draft);
  const stack = layout === 'stack';

  const patch = (part: Partial<WorklogDraft>): void => {
    onChange({ ...draft, ...part });
  };

  return (
    <div className={stack ? 'flex flex-col gap-3' : 'flex flex-wrap items-end gap-2'}>
      {lead}

      {!showDate ? null : (
        <Labelled label={t('time.column.date')} stack={stack} width="w-[128px]">
          {/* ⚠️ No `id`/`htmlFor`: the picker is a button that opens a dialog, and a `<label for>`
              pointing at one names the wrong thing. `label` names it instead. */}
          <DateField
            label={t('time.column.date')}
            value={draft.workedOn}
            clearable={false}
            onChange={(workedOn) => {
              patch({ workedOn });
            }}
          />
        </Labelled>
      )}

      <Labelled
        label={t('time.log.startedAt')}
        stack={stack}
        width="w-[92px]"
        tourId="worklog-start"
      >
        <TextInput
          value={draft.startedAt}
          aria-label={t('time.log.startedAt')}
          onChange={(event) => {
            patch({ startedAt: event.target.value });
          }}
          placeholder={START_OF_DAY}
        />
      </Labelled>

      <Labelled
        label={t('time.log.duration')}
        stack={stack}
        width="w-[92px]"
        tourId="worklog-duration"
      >
        <TextInput
          value={draft.hours}
          aria-label={t('time.log.duration')}
          onChange={(event) => {
            patch({ hours: event.target.value });
          }}
          placeholder="8:00"
          title={t('time.hours.help')}
        />
      </Labelled>

      {/* ⚠️ Absent where the workspace has defined no type, because a claim it could raise is one
          the server would refuse. */}
      {!overtimeOffered ? null : (
        <Labelled
          label={t('time.column.overtime')}
          stack={stack}
          width="w-[190px]"
          tourId="worklog-overtime"
        >
          <Select
            aria-label={t('time.column.overtime')}
            value={draft.overtime ? 'yes' : 'no'}
            onChange={(event) => {
              patch({ overtime: event.target.value === 'yes' });
            }}
            /* ⚠️⚠️ **TWO OPTIONS, AND NO RATES.** The three types with their multipliers used to
               be the choices here — see `overtime` on the draft for why they are not. */
            options={[
              { value: 'no', label: t('time.log.noOvertime') },
              { value: 'yes', label: t('time.log.isOvertime') },
            ]}
          />
        </Labelled>
      )}

      <Labelled
        label={t('time.log.note')}
        stack={stack}
        width="min-w-[160px] flex-1"
        tourId="worklog-note"
      >
        <TextInput
          value={draft.note}
          aria-label={t('time.log.note')}
          onChange={(event) => {
            patch({ note: event.target.value });
          }}
          placeholder={t('time.log.notePlaceholder')}
        />
      </Labelled>

      {/* ⚠️ Below the entry's own fields — a workspace's additions come after what the product
          itself defines. Renders nothing when none are defined. */}
      {metafields.length === 0 ? null : (
        <div className={stack ? '' : 'w-full'}>
          <MetafieldSection
            fields={metafields}
            values={draft.metafields}
            /* A worklog's `user` metafield has no list to offer here. */
            members={[]}
            onChange={(values) => {
              patch({ metafields: values });
            }}
          />
        </div>
      )}

      {/* ⚠️⚠️ **NO EXPLANATORY LINE UNDER THE MARKING, AND THERE WAS ONE.** It read *"a
          rögzítéssel túlórakérelem indul"* — a sentence restating what the control had just been
          used to do. The product owner has struck it along with the credit hint next door; the
          rule is that a control does not narrate itself. */}

      {/* ⚠️ What will be saved, or why it cannot be — one line doing both jobs, as the calendar's
          dialog has always done. Two messages would mean a valid form shows nothing and an invalid
          one shows an error, which is one more state than the reader needs. */}
      {draft.hours.trim() === '' ? null : (
        <p
          className={`w-full text-[12px] ${hours === null ? 'text-destructive' : 'text-muted-foreground'}`}
          role={hours === null ? 'alert' : undefined}
        >
          {hours === null
            ? t('logs.entry.badDuration')
            : t('logs.entry.willSave', { hours: formatHours(hours) })}
        </p>
      )}
    </div>
  );
}

/** ⚠️ One label treatment for both layouts — the stack's is the row's without the fixed width. */
function Labelled({
  label,
  stack,
  width,
  tourId,
  children,
}: {
  readonly label: string;
  readonly stack: boolean;
  readonly width: string;
  /** A stable hook for the guided tour — F-2. */
  readonly tourId?: string;
  readonly children: ReactNode;
}) {
  return (
    <div className={stack ? '' : width} data-tour={tourId}>
      <span className="text-muted-foreground mb-1 block text-[11.5px] font-medium">{label}</span>
      {children}
    </div>
  );
}
